import {
  Box,
  Chip,
  Loader,
  TableSortable,
  Typography,
} from "@saviynt/design-system";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { getSortConfigs, tableColumns } from "./PoliciesFindingsListConfig";
import { capitalizeFirstLetter } from "@repo/mfe-core";
import { useInfiniteScroll } from "@repo/mfe-core/host";
import {
  formatTtrToDays,
  LinkCell,
  TextCell,
} from "../../PoliciesPageList/CellRenderer/CellRenderer";
import { getPoliciesColor } from "../../PoliciesPageList/PoliciesPageListColumnConfig";
import PoliciesSidepanel from "../../PoliciesSidepanel/PoliciesSidepanel";
import { EmptyState, TableSkeletonLoader } from "@repo/ui";
import InventorySidePanel from "../../PoliciesForm/InventorySidePanel/InventorySidePanel";
import { usePolicyFindingsList } from "../../../hooks/usePolicyFindings";
import { useFindingsData } from "../context/FindingsDataContext";
import { useSelection } from "../context/SelectionContext";
import { IdentityCell, RemediationCell, ActionCell } from "./CellRenderer";
import {
  FindingsRequestPayload,
  PolicyFindingDetail,
} from "@repo/api/schemas/policiesFindings.schema";
import "./PoliciesFindingsList.css";
import { useSidecar } from "../../../hooks/useSidecar";

const INITIAL_SIDECAR_DATA = {
  id: "",
  assetName: "",
  connectionName: "",
  displayName: "",
  source: "",
  activeTabId: "1",
};

const DEFAULT_BRIDGE_POSITION = { top: 0, left: 0, height: 0 };

export default function PoliciesFindingsList({
  payload,
  onPayloadChange,
  remediationPermission = false,
  category = "",
}: {
  payload: FindingsRequestPayload;
  onPayloadChange?: (newPayload: FindingsRequestPayload) => void;
  remediationPermission?: boolean;
  category?: string;
}) {
  const intl = useIntl();
  const [activePanel, setActivePanel] = useState<number | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<
    number | undefined
  >();
  const [bridgePosition, setBridgePosition] = useState(DEFAULT_BRIDGE_POSITION);
  const [columns, setColumns] = useState(
    tableColumns.map((col) =>
      col.key === "identityDisplayName" && category === "NHI_POSTURE"
        ? { ...col, hasCheckbox: remediationPermission }
        : col,
    ),
  );

  const { isSidecarOpen, sidecarMetaData, closeSidecar, openAssetSidecar } =
    useSidecar(INITIAL_SIDECAR_DATA);
  const {
    isSidecarOpen: isPolicySidecarOpen,
    sidecarMetaData: policySidecarMetaData,
    closeSidecar: closePolicySidecar,
    openAssetSidecar: openPolicySidecar,
  } = useSidecar<{ id: string; name: string }>({
    id: "",
    name: "",
  });

  const { selectedRows, setSelectedRows } = useSelection();
  const { setRecordsCount } = useFindingsData();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    usePolicyFindingsList({ ...payload });

  const observerRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const tableData = useMemo(
    () => data?.pages.flatMap((page) => page.values) || [],
    [data],
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [payload, setSelectedRows]);

  useEffect(() => {
    setRecordsCount({
      loadedRecordsCount: tableData.length,
      totalRecordsCount: data?.pages[0]?.totalCount || 0,
    });
  }, [tableData.length, setRecordsCount, data?.pages]);

  const updateBridgePosition = useCallback((rowIndex: number) => {
    const tableRow = document.querySelector(
      `[data-testid="Policy-Findings-Table"] tbody tr:nth-child(${rowIndex + 1})`,
    );
    if (tableRow) {
      const rect = tableRow.getBoundingClientRect();
      setBridgePosition({
        top: rect.top + rect.height / 2,
        left: 0,
        height: rect.height,
      });
    }
  }, []);

  const openSidecarAtRow = useCallback(
    (rowIndex: number, panel: number) => {
      setSelectedRowIndex(rowIndex);
      setTimeout(() => updateBridgePosition(rowIndex), 0);
      setActivePanel(panel);
    },
    [updateBridgePosition],
  );

  const handlePolicyNameClick = useCallback(
    (e: React.MouseEvent, row: PolicyFindingDetail, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      openPolicySidecar({
        id: row.triggerPolicyId,
        name: row.policyName,
      });
      openSidecarAtRow(index, 2);
    },
    [openPolicySidecar, openSidecarAtRow],
  );

  const handleCloseSidecar = useCallback(() => {
    closeSidecar();
    setSelectedRowIndex(undefined);
    setBridgePosition(DEFAULT_BRIDGE_POSITION);
  }, [closeSidecar]);

  const tableRows = useMemo(
    () =>
      tableData.map((row, index) => ({
        id: row.violationId,
        identityDisplayName: <IdentityCell row={row} />,
        policyName: (
          <LinkCell
            text={row.policyName}
            onClick={(e) => handlePolicyNameClick(e, row, index)}
          />
        ),
        severity: (
          <Chip
            kind="subtleFilled"
            label={capitalizeFirstLetter(row.severity)}
            color={getPoliciesColor(row.severity)}
            className={row.severity}
          />
        ),
        status: <TextCell text={capitalizeFirstLetter(row.status)} />,
        ttr: <TextCell text={row.ttr ? formatTtrToDays(row.ttr) : "-"} />,
        remediation: (
          <RemediationCell remediationData={row.remediationData} intl={intl} />
        ),
        tags: <TextCell text={row.tags || null} />,
        actionColumn: (
          <ActionCell row={row} remediationPermission={remediationPermission} />
        ),
      })),
    [handlePolicyNameClick, intl, remediationPermission, tableData],
  );

  const handleRowClick = useCallback(
    // @ts-expect-error: Need to verify type of row data
    (_row: PolicyFindingDetail, rowIndex: number) => {
      const originalData = tableData[rowIndex];
      if (originalData.category !== "NHI_POSTURE") {
        return null;
      }
      const {
        identityDisplayName,
        nativeType,
        nativeSource,
        assetId,
        identityName,
      } = originalData;

      openAssetSidecar({
        id: assetId,
        assetName: identityName,
        displayName: identityDisplayName,
        connectionName: nativeType,
        source: nativeSource,
        activeTabId: "1",
      });
      openSidecarAtRow(rowIndex, 1);
    },
    [openAssetSidecar, openSidecarAtRow, tableData],
  );

  const handleColumnSort = useCallback(
    (columnKey: string, order: string) => {
      onPayloadChange?.({
        ...payload,
        sortBy: columnKey,
        sortOrder: order.toUpperCase(),
      });
    },
    [payload, onPayloadChange],
  );

  const handleScroll = useCallback(() => {
    if (selectedRowIndex !== undefined) {
      updateBridgePosition(selectedRowIndex);
    }
  }, [selectedRowIndex, updateBridgePosition]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  if (isLoading)
    return (
      <Box className="p-8">
        <TableSkeletonLoader rows={10} />
      </Box>
    );

  const anySidecarOpen = isSidecarOpen || isPolicySidecarOpen;

  return (
    <>
      {isSidecarOpen && (
        <InventorySidePanel
          isSidecarOpen={isSidecarOpen}
          closeSidecar={handleCloseSidecar}
          sidecarMetaData={sidecarMetaData}
          bridgePosition={bridgePosition}
          variant="findings"
          className={activePanel === 1 ? "Inventory-SidePanel--active" : ""}
          isCloseButtonHidden={isPolicySidecarOpen && activePanel === 1}
        />
      )}
      {isPolicySidecarOpen && (
        <PoliciesSidepanel
          isOpen={isPolicySidecarOpen}
          closeSidepanel={closePolicySidecar}
          policyData={policySidecarMetaData}
          bridgePosition={bridgePosition}
          variant="findings"
          className={activePanel === 2 ? "Policy-SidePanel--active" : ""}
          isCloseButtonHidden={isSidecarOpen && activePanel === 2}
        />
      )}
      <Box className="px-12 py-4">
        {tableData.length > 0 ? (
          <>
            <Typography
              kind="body3"
              desktop="body3-d"
              mobile="body3-d"
              color="--color-foreground-neutral-subtle"
              className="mb-4"
            >
              {intl.formatMessage(
                { id: "ttMzf4", defaultMessage: "1-{count} of {total}" },
                {
                  count: tableData.length,
                  total: data?.pages[0]?.totalCount,
                },
              )}
            </Typography>
            <TableSortable
              columnConfigs={{ isDndDisabled: true }}
              columns={columns}
              setColumns={setColumns}
              data={tableRows}
              sortConfigs={getSortConfigs(intl)}
              selectedRowIndex={selectedRowIndex}
              onSort={handleColumnSort}
              onRowClick={handleRowClick}
              selectedRows={remediationPermission ? selectedRows : []}
              onRowSelect={remediationPermission ? setSelectedRows : undefined}
              dataTestId="Policy-Findings-Table"
              className={classNames("Policy-Findings-Table", {
                "Policy-Findings-Table--shrinked": anySidecarOpen,
                "Policy-Findings-Table--expanded": !anySidecarOpen,
              })}
            />
          </>
        ) : (
          <Box className="NHI-Dashboard-Container-Table-NoRecordFound">
            <EmptyState />
          </Box>
        )}
        {isFetchingNextPage && (
          <div className="Nhi-InventoryList-loader" data-testid="loader-loop">
            <Loader kind="loop" format="main" color="brand" />
          </div>
        )}
        {hasNextPage && <div ref={observerRef} style={{ height: "1px" }} />}
      </Box>
    </>
  );
}
